import type { I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import type { AltPriceComponent, PriceCheckResponse, PriceComponent } from "@/types/api";

/**
 * Gets the currency symbol for a given currency code using Intl.NumberFormat
 * @param currencyCode - ISO 4217 currency code (e.g., "EUR", "USD", "CHF")
 * @param locale - Locale string (e.g., "en-US", "de-DE"). Defaults to "en-US"
 * @returns The currency symbol (e.g., "€", "$", "CHF")
 */
export function getCurrencySymbol(currencyCode: string, locale: string = "en-US"): string {
  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    const parts = formatter.formatToParts(0);
    const currencyPart = parts.find((part) => part.type === "currency");
    return currencyPart?.value || currencyCode;
  } catch {
    // Fallback to currency code if formatting fails
    return currencyCode;
  }
}

/**
 * Formats a price with currency symbol using Intl.NumberFormat
 * @param amount - The amount to format
 * @param currencyCode - ISO 4217 currency code
 * @param locale - Locale string. Defaults to "en-US"
 * @returns Formatted price string (e.g., "€123.45")
 */
export function formatCurrency(
  amount: number,
  currencyCode: string,
  locale: string = "en-US",
): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // Fallback formatting
    return `${currencyCode} ${amount.toFixed(2)}`;
  }
}

export function getAltPrice(
  objectId: number,
  index: number,
  altComponents: AltPriceComponent[],
): number {
  if (!altComponents || altComponents.length <= index) {
    return 0;
  }
  const altComponent = altComponents[index];
  if (altComponent?.object_id === objectId) {
    return altComponent.price;
  }
  return 0;
}

export function groupComponentsByObjectId(components: PriceComponent[]): PriceComponent[][] {
  const groups = new Map<string, PriceComponent[]>();

  components.forEach((component) => {
    const objectId = component.object_id;
    const groupKey = `${objectId}-${component.included ? "included" : "paid"}`;
    if (!groups.has(groupKey)) {
      groups.set(groupKey, [component]);
    } else {
      groups.get(groupKey)?.push(component);
    }
  });
  return Array.from(groups.values());
}

export function groupComponentsByName(components: PriceComponent[]): PriceComponent[][] {
  const groups = new Map<string, PriceComponent[]>();

  components.forEach((component) => {
    const groupKey = `${component.name}-${component.included ? "included" : "paid"}`;
    if (!groups.has(groupKey)) {
      groups.set(groupKey, [component]);
    } else {
      groups.get(groupKey)?.push(component);
    }
  });
  return Array.from(groups.values());
}

export function formatPaymentInfo(paymentSystem: string, i18n: I18n): string {
  const message = {
    ON_SITE: msg`Pay at the campsite on arrival`,
    INVOICE: msg`Pay when the campsite sends you an invoice`,
    ONLINE: msg`Pay online now`,
  }[paymentSystem];
  return message ? i18n._(message) : "";
}

export function formatPriceComponentType(type: string, i18n: I18n): string {
  const message = {
    campsite: msg`Accommodation`,
    person: msg`Guests`,
    extra: msg`Extras`,
  }[type];
  return message ? i18n._(message) : "";
}

export function computeAmountToPayOnline(price: PriceCheckResponse): number {
  if (price.deposit_percent && price.deposit_value < price.total) {
    return price.deposit_value;
  }
  return price.total;
}
